import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzButtonModule } from 'ng-zorro-antd/button';
import {
  BirthdayGreetingService,
  BirthdaySendResult,
  BirthdaySendStatus,
  BirthdayTemplate,
  birthdayErrorMessage,
} from '@beautyapp/core';

/**
 * Georgian labels for every `BirthdaySendStatus`, copied word-for-word from
 * `marketing.birthday.status.*` in ka.json so this panel and the birthdays
 * list say the same thing about why nothing was sent. Kept as a literal map
 * rather than pulling in `TranslateModule` — every screen this panel is used
 * on so far uses literal Georgian strings, and matching that is a standing
 * constraint.
 */
const STATUS_LABELS: Record<BirthdaySendStatus, string> = {
  SENT: 'გაიგზავნა',
  ALREADY_SENT: 'დღეს უკვე მიულოცეს',
  NO_PHONE: 'ტელეფონის ნომერი არ აქვს',
  NO_BRANCH: 'ფილიალი ვერ დადგინდა',
  NO_SENDER: 'ფილიალს SMS გამგზავნი არ აქვს',
  NO_TEMPLATE: 'ამ ენაზე აქტიური შაბლონი არ არის',
  NOT_ELIGIBLE:
    'არ არის შესაფერისი (არ აქვს დაბადების თარიღი, ტელეფონის ნომერი ან არ არის ამ ორგანიზაციის მომხმარებელი)',
  FAILED: 'შეცდომა',
};

/**
 * Standalone "send a birthday greeting to this one customer" control.
 *
 * Extracted from `Customer360CommunicationsComponent` (beaut-308) so the
 * information tab can offer the same control next to the customer's birth
 * date without duplicating any of its hard-won behaviour: a Georgian label
 * for every `BirthdaySendStatus`, the server's own error message via
 * `birthdayErrorMessage()`, the `isActive`-template filter with the default
 * pre-selected, the organization-level feature switch (fail-open on a
 * settings-fetch error), and the in-flight guard against a double send.
 *
 * Moved rather than copied: the next fix to any of this lands in one place.
 * Two screens that disagree about why a send failed is a defect this
 * feature has already been reviewed for three times.
 */
@Component({
  selector: 'beautyapp-birthday-send-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, NzIconModule, NzSelectModule, NzButtonModule],
  template: `
    <div class="send-greeting">
      <nz-select
        [ngModel]="templateId()"
        (ngModelChange)="templateId.set($event)"
        nzPlaceHolder="შაბლონი"
        class="send-greeting__select"
      >
        <nz-option
          *ngFor="let t of templates()"
          [nzValue]="t.id"
          [nzLabel]="t.name + ' (' + (t.language | uppercase) + ')'"
        ></nz-option>
      </nz-select>
      <button
        nz-button
        nzType="primary"
        data-test="send-birthday-greeting"
        [nzLoading]="sending()"
        [disabled]="sending() || !templateId() || featureEnabled() === false"
        (click)="sendGreeting()"
      >
        <span nz-icon nzType="gift"></span>
        მილოცვის გაგზავნა
      </button>
      <!--
        No templates — either none exist yet, or listTemplates() errored
        and the catch set templates to []. Either way the operator should
        still see this control exists, not have it vanish. The birthday
        list screen does the same: button visible, disabled, hint beside it.
      -->
      <span class="send-greeting__hint" *ngIf="!templates().length" data-test="no-templates-hint">
        შაბლონი არ არის — დაამატეთ დაბადების დღის პარამეტრებში
      </span>
      <!--
        beaut-308 walk finding 2. getSettings() resolving to isActive:
        false used to be discoverable only from the server's 422 after
        the operator already tried to send. Same shape as the
        no-templates hint above: button visible, disabled, reason shown.
      -->
      <span
        class="send-greeting__hint"
        *ngIf="featureEnabled() === false"
        data-test="feature-disabled-hint"
      >
        დაბადების დღეების მილოცვა გამორთულია — ჩართეთ პარამეტრებში
      </span>
    </div>
  `,
  styles: [`
    .send-greeting { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
    .send-greeting__select { min-width: 200px; }
    .send-greeting__hint { font-size: 13px; color: #595959; }
  `],
})
export class BirthdaySendPanelComponent {
  userId = input.required<string>();

  /** Fires after a `SENT` result so the host can reload whatever log it shows. */
  sent = output<void>();

  private msg = inject(NzMessageService);
  private greetings = inject(BirthdayGreetingService);

  templates = signal<BirthdayTemplate[]>([]);
  templateId = signal<string | undefined>(undefined);
  sending = signal(false);

  /**
   * beaut-308 walk finding 2. `undefined` until `getSettings()` answers, so
   * the button does not flash disabled-then-enabled on a normal load — see
   * `canSend` semantics on `BirthdaysComponent`, mirrored here.
   */
  featureEnabled = signal<boolean | undefined>(undefined);

  constructor() {
    this.loadTemplates();
    this.loadSettings();
  }

  private loadSettings(): void {
    this.greetings.getSettings().subscribe({
      next: (settings) => this.featureEnabled.set(settings.isActive),
      // Fail open: a settings-fetch hiccup is not, on its own, a reason to
      // block sending — the server still enforces `isActive` on every
      // actual send regardless of what this flag shows.
      error: () => undefined,
    });
  }

  private loadTemplates(): void {
    this.greetings.listTemplates().subscribe({
      next: (rows) => {
        const active = (rows ?? []).filter((row) => row.isActive);
        this.templates.set(active);
        this.templateId.set(active.find((row) => row.isDefault)?.id ?? active[0]?.id);
      },
      error: () => this.templates.set([]),
    });
  }

  /**
   * One customer, one message, any day. The backend's manual path applies no
   * date rule and no dedupe, so what comes back is the whole truth about
   * whether anything was sent — and six of the eight statuses mean it was not.
   * Every one of them is shown. A button that reports nothing is how this
   * feature already failed once.
   */
  sendGreeting(): void {
    const id = this.templateId();

    if (this.sending() || !id || this.featureEnabled() === false) {
      return;
    }

    this.sending.set(true);

    this.greetings.send([this.userId()], id).subscribe({
      next: (results) => {
        this.sending.set(false);
        const result = results?.[0];

        if (result?.status === 'SENT') {
          this.msg.success('მილოცვა გაიგზავნა');
          this.sent.emit();

          return;
        }

        this.msg.error(
          result
            ? this.failureMessage(result)
            : 'ვერ გაიგზავნა — სერვერმა პასუხი არ დააბრუნა',
        );
      },
      // beaut-308 walk finding 1. The server's own Georgian explanation
      // (e.g. the isActive-false refusal) is preferred over the generic
      // fallback below — never `error.message`, which is Angular's
      // developer-facing string ("Http failure response for .../send: 422
      // OK"), meaningless to an operator.
      error: (error) => {
        this.sending.set(false);
        this.msg.error(birthdayErrorMessage(error) ?? 'მილოცვა ვერ გაიგზავნა');
      },
    });
  }

  /**
   * The birthdays list renders `marketing.birthday.status.*` for the same
   * `BirthdaySendStatus`, in Georgian. This card used to fall back to the
   * backend's raw English `reason` (or a bare `NO_SENDER`) here, so the two
   * screens disagreed about the single most important thing on them: why
   * nothing was sent. The Georgian label is now primary; `reason`, when the
   * backend sent one, is kept as secondary detail in parentheses.
   */
  private failureMessage(result: BirthdaySendResult): string {
    const label = STATUS_LABELS[result.status] ?? result.status;

    return result.reason ? `ვერ გაიგზავნა: ${label} (${result.reason})` : `ვერ გაიგზავნა: ${label}`;
  }
}
